import React, { useState, useEffect, useRef } from 'react';
import { LineList } from './LineList';
import styles from './MeasureToolContainer.module.scss';
import { TextStyleTool } from './TextStyleTool';
import { ColorPanel } from './ColorPanel';
import { ButtonV2 } from '../../components/ButtonV2/ButtonV2';
import {
  apiPatchToggleImageMeasure,
  apiGetImageMeasurementData,
  apiPatchImageMeasurementData,
  apiPostImageMeasurementData,
  apiPatchPublicImageMeasurementData,
} from '../../helpers/api';
import Loader from '../Loader';
import { dispatcher } from '../../helpers/projectHelper';
import { successToast } from '../../helpers/toastHelper';
import { measureToolActions, selectIsStampingAllowed, selectShowUnit, selectUnitValue } from '../../redux/slicers/measureToolSlicer';
import SaveFloppy from '../../assets/images/saveFloppy.svg';
import TickWhite from '../../assets/images/tickWhite.svg';
import { useSelector } from 'react-redux';
import { CustomSelectTile } from '../../components/admin/curator/components/TileGrout/CustomSelect';
import { LayoutControl } from './LayoutControl';
import { getLocalStorage, setLocalStorage, removeLocalStorage } from '../../helpers/jsHelper';

const MEASURE_TOOL_LAYOUT_KEY = 'MEASURE_TOOL_LAYOUT';

export const MeasureToolContainer = ({
  allStyleSectionData,
  isMeasureToolActive,
  setIsMeasureToolActive,
  isMeasureToolEditable,
  activeCameraId,
  lineValues,
  currentCard,
  toggleOffMeasureTool,
  isFullScreen,
}) => {
  const [selectedLine, setSelectedLine] = useState({ name: 'Line-2', value: 2 });
  const [styleSlected, setStyleSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const wrapperRef = useRef(null);
  const isStampingAllowed = useSelector(selectIsStampingAllowed);
  const showUnit = useSelector(selectShowUnit);
  const [allValues, setAllValues] = useState([]);
  const [selectedUnitValue, setSelectedUnitValue] = useState()
  const unitValue = useSelector(selectUnitValue);
  const [isLayoutDefault, setIsLayoutDefault] = useState(false);

  useEffect(() => {
    const vals = [
      { value: 'inches', label: 'inches' },
      { value: 'feet', label: 'feet' },
      { value: 'cm', label: 'centimeter' },
    ];
    setAllValues([...vals]);
    const unitValToDisplay = unitValue ? vals?.find((data) => data?.value === unitValue) : vals?.[0];
    setSelectedUnitValue(unitValToDisplay);
    dispatcher(measureToolActions.setUnitValue(unitValToDisplay?.value));

    const savedLayout = getLocalStorage(MEASURE_TOOL_LAYOUT_KEY);
    if (savedLayout === 'inline' || savedLayout === 'offset') {
      setIsLayoutDefault(true);
      updateStyleData('layout', savedLayout);
    }
  }, []);

  useEffect(() => {
    if (allStyleSectionData?.lineSize !== selectedLine?.value) {
      const val = allStyleSectionData?.lineSize;
      setSelectedLine({ name: 'Line-' + val, value: val });
    }
    if (allStyleSectionData?.textStyle !== styleSlected) {
      const val = allStyleSectionData?.textStyle;
      setStyleSelected(val);
    }
  }, [allStyleSectionData]);

  // close the color panel on click of area outside dropdown
  const detectOuterAreaClick = (ref) => {
    useEffect(() => {
      function ifMeasureToolIconClicked(event) {
        if (event?.target?.parentElement?.innerText === 'Add Dimensions') {
          return true;
        } else {
          return false;
        }
      }
      function ifMeasureToolContainerClicked(event) {
        if (event.target.classList.value.includes('MeasureToolContainer')) {
          return true;
        } else {
          return false;
        }
      }
      function handleClickOutside(event) {
        if (
          ref.current &&
          !ref.current.contains(event.target) &&
          !ifMeasureToolIconClicked(event) &&
          !ifMeasureToolContainerClicked(event)
        ) {
          toggleOffMeasureTool();
        }
      }
      // Bind the event listener
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        // Unbind the event listener on clean up
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [ref]);
  };
  detectOuterAreaClick(wrapperRef);

  const selectLine = (val) => {
    setSelectedLine(val);
    updateStyleData('lineSize', val.value);
  };

  const selectStyle = (val) => {
    let dataToUpdate;
    if (val === styleSlected) {
      dataToUpdate = null;
    } else {
      dataToUpdate = val;
    }
    setStyleSelected(dataToUpdate);
    updateStyleData('textStyle', dataToUpdate);
  };

  const changeFontSize = (e) => {
    let value = e.target.value;
    value = value.replace(/[^#0-9]/gi, '');
    if (value === '') {
      value = 0;
    }
    if (value > 60) {
      value = 60;
    }
    updateStyleData('textSize', value);
  };

  const updateStyleData = (key, value) => {
    const toolDataCopy = JSON.parse(JSON.stringify(allStyleSectionData));
    if (key === 'color') {
      toolDataCopy['lineColor'] = value;
      toolDataCopy['textColor'] = value;
    } else {
      toolDataCopy[key] = value;
    }
    dispatcher(measureToolActions.setMeasureToolStyle(toolDataCopy));
    let copyValues = JSON.parse(JSON.stringify(lineValues));
    copyValues.measurements = copyValues.measurements.map((data) => {
      const existingEdgeAngle = data?.styleApplied?.edgeAngle;
      data['styleApplied'] = {
        ...toolDataCopy,
        edgeAngle: typeof existingEdgeAngle === 'number' ? existingEdgeAngle : null,
      };
      return data;
    });
    dispatcher(measureToolActions.setAllLineValues(copyValues));
  };

  const addTheLine = (e) => {
    updateToggleData(false, true);
    dispatcher(measureToolActions.setAddLineClicked(true));
  };

  const updateToggleData = async (val, activeValue) => {
    if (isMeasureToolEditable) {
      if (val) {
        setLoading(true);
      }
      const formData = new FormData();
      formData.append('is_measurement', activeValue);
      const receivedData = await apiPatchToggleImageMeasure(activeCameraId, formData);
      if (receivedData?.id) {
        setIsMeasureToolActive(!val ? true : receivedData?.is_measurement);
        if (val) {
          setLoading(false);
        }
      }
    } else {
      setIsMeasureToolActive(!isMeasureToolActive);
    }
  };

  const saveTheData = () => {
    setSaveLoading(true);
    const payloadToSave = JSON.parse(JSON.stringify(lineValues));
    payloadToSave['roomId'] = currentCard?.room;
    payloadToSave['imageId'] = currentCard?.id;
    payloadToSave['unitValue'] = unitValue || 'inches';
    // payloadToSave['isStampingAllowed'] = isStampingAllowed;
    payloadToSave.measurements = payloadToSave.measurements.map((data) => {
      data.selected = false;
      return data;
    });
    updateApiData(payloadToSave);
  };

  const updateApiData = async (data) => {
    const dbData = await apiGetImageMeasurementData(activeCameraId);

    let result;
    const formData = new FormData();
    formData.append('render_image', activeCameraId);
    formData.append('data', JSON.stringify(data));
    if (dbData?.results?.[0]?.data) {

      // Here, i'm making is_dimension_shot_file: false. It means that the image needs to be uploaded again.
      // This is done because on Product V2 page we are showing the uploaded image of the dimension shot when this key is true.
      // But when the image dimensions are modified, it needs to be uploaded again.
      const id = dbData?.results?.[0]?.id;
      await apiPatchPublicImageMeasurementData(id, { is_dimension_shot_file: false }); // making is_dimension_shot_file: true when the image is successfully uploaded

      result = await apiPatchImageMeasurementData(activeCameraId, formData);
    } else {
      result = await apiPostImageMeasurementData(formData);
      if (result?.data) {
        successToast('Data saved successfully');
      }
    }
    if (result?.data?.data) {
      dispatcher(measureToolActions.setImageMeasurementData(result?.data?.data));
    }
    setSaveLoading(false);
  };

  const changeColor = (val) => {
    updateStyleData('color', val);
  };

  const updateStamping = (val) => {
    dispatcher(measureToolActions.setIsStampingAllowed(val));
  };

  const updateShowUnit = (val) => {
    dispatcher(measureToolActions.setShowUnit(val));
  };

  let styleObj = {};

  if (!isMeasureToolEditable) {
    styleObj = {
      width: '200px',
    };
  }

  // get Measure tool icon position.
  // Open the toolbox at the same position
  const div = document.getElementById('measureTool');
  if (div) {
    const rect = div?.getBoundingClientRect();
    // 250 is sidebar width, 100 is to keep it to center
    let centerValue = rect.left - 100;
    styleObj['left'] = isFullScreen ? (centerValue - 120): (centerValue - 250);
  }

  const customStyle = {
    menu: { color: '#000', padding: '0px !important', margin: '0px !important' },
    control: { minHeight: '16px' },
    input: { padding: '0px 8px'}
  };

  const handleUnitChange = (val) => {
    setSelectedUnitValue(val);
    dispatcher(measureToolActions.setUnitValue(val?.value));
  }

  const handleLayoutChange = (nextLayout) => {
    updateStyleData('layout', nextLayout);
    if (isLayoutDefault) {
      setLocalStorage(MEASURE_TOOL_LAYOUT_KEY, nextLayout);
    }
  };

  const handleToggleLayoutDefault = (nextOn) => {
    setIsLayoutDefault(nextOn);
    if (nextOn) {
      setLocalStorage(MEASURE_TOOL_LAYOUT_KEY, allStyleSectionData?.layout || 'inline');
    } else {
      removeLocalStorage(MEASURE_TOOL_LAYOUT_KEY);
    }
  };

  return (
    <div className={styles.mainContainer} ref={wrapperRef} style={styleObj}>
      {isMeasureToolEditable && (
        <div className={styles.flexDisp} style={{ gap: '10px' }}>
          <div className={styles.textDiv} id="textStyles">
            <TextStyleTool
              selectStyle={(val) => selectStyle(val)}
              styleSlected={styleSlected}
              allStyleSectionData={allStyleSectionData}
              changeFontSize={(val) => changeFontSize(val)}
            />
          </div>
          <div className={styles.saveBtnDiv} id="textStyles">
            <ButtonV2
              onClick={saveTheData}
              className={`${!isMeasureToolActive ? styles.disableSave : styles.saveBtn}`}
              loading={saveLoading}
              disabled={!isMeasureToolActive}>
              <div
                className={`${styles.flexDisp} ${
                  !isMeasureToolActive ? styles.reduceOpacity : ''
                }`}>
                <div>
                  <img src={SaveFloppy} height={20} width={20} style={{ marginRight: '8px' }} />
                </div>
                <div className={styles.addLineTxt} style={{ marginTop: '3px' }}>
                  Save
                </div>
              </div>
            </ButtonV2>
          </div>
        </div>
      )}
      <div className={styles.toolDiv}>
        {isMeasureToolEditable && (
          <div className={styles.divisionDiv}>
            <LineList selectedLine={selectedLine} selectLine={(val) => selectLine(val)} />
            <ColorPanel
              allStyleSectionData={allStyleSectionData}
              changeColorVal={(val) => changeColor(val)}
            />
          </div>
        )}
        {isMeasureToolEditable && (
          <LayoutControl
            layout={allStyleSectionData?.layout || 'inline'}
            onChange={handleLayoutChange}
            isDefault={isLayoutDefault}
            onToggleDefault={handleToggleLayoutDefault}
          />
        )}
        {isMeasureToolEditable && (
          <ButtonV2 onClick={addTheLine} className={styles.addLineBtn} color="primary">
            <span className={styles.addLineTxt}>Add Line</span>
          </ButtonV2>
        )}
        <div className={styles.divisionDiv} style={{ paddingTop: '4px' }}>
          <div
            className={styles.showDimensionParent}
            style={{ justifyContent: 'flex-start', marginLeft: '2px' }}>
            <div
              className={`${styles.flexDisp} ${styles.activeClass} ${
                isMeasureToolActive ? styles.activeBackground : ''
              }`}
              onClick={() => updateToggleData(true, !isMeasureToolActive)}>
              {isMeasureToolActive && !loading && <img src={TickWhite} height={15} width={15} />}
              <Loader
                position={'relative'}
                style={{ left: '0px' }}
                loading={loading}
                color="inherit"
                size={16}
                thickness={2}
                borderColor={isMeasureToolActive ? '#fff' : null}
              />
            </div>
            <div className={styles.checkBoxTxt}>Show Dimensions</div>
          </div>
          {isMeasureToolEditable && (
            <div
              className={styles.showDimensionParent}
              style={{ justifyContent: 'flex-start', marginRight: '4px' }}>
              <div
                className={`${styles.flexDisp} ${styles.activeClass} ${
                  isStampingAllowed ? styles.activeBackground : ''
                }`}
                onClick={() => updateStamping(!isStampingAllowed)}>
                {isStampingAllowed && <img src={TickWhite} height={15} width={15} />}
              </div>
              <div className={styles.checkBoxTxt}>45&deg; Snapping</div>
            </div>
          )}
        </div>
        <div className={styles.divisionDiv} style={{ paddingTop: '4px' }}>
          {isMeasureToolEditable && (
            <div
              className={styles.showDimensionParent}
              style={{ justifyContent: 'flex-start', marginLeft: '2px' }}>
              <div
                className={`${styles.flexDisp} ${styles.activeClass} ${
                  showUnit ? styles.activeBackground : ''
                }`}
                onClick={() => updateShowUnit(!showUnit)}>
                {showUnit && <img src={TickWhite} height={15} width={15} />}
              </div>
              <div className={styles.checkBoxTxt}>Show Unit</div>
            </div>
          )}
          {isMeasureToolEditable && (
            <div style={{width: '100%'}}>
              <CustomSelectTile
                options={allValues}
                value={selectedUnitValue}
                onChange={handleUnitChange}
                placeholder="Select unit"
                customStyle={customStyle}
                customMenu={false}
                menuPlacement="top"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
